import { useEffect, useState } from "react";
import { toast } from "sonner";

import { endpoints, extractErrorAr } from "@/lib/api";
import Icon from "@/components/Icon";

const PALESTINIAN_CITIES = [
  "رام الله", "نابلس", "الخليل", "بيت لحم", "جنين", "طولكرم",
  "قلقيلية", "أريحا", "طوباس", "سلفيت", "غزة", "القدس",
];

export default function QuoteForm({
  services = [],
  defaultServiceSlug = "",
  productId = null,
  productName = "",
  onSuccess,
  compact = false,
  testIdPrefix = "quote-form",
}) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    service_slug: defaultServiceSlug || "",
    city: "",
    message: productName ? `أرغب بعرض سعر للمنتج: ${productName}` : "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { ref }

  useEffect(() => {
    if (defaultServiceSlug) {
      setForm((f) => ({ ...f, service_slug: defaultServiceSlug }));
    }
  }, [defaultServiceSlug]);

  useEffect(() => {
    if (productName) {
      setForm((f) => ({
        ...f,
        message: f.message || `أرغب بعرض سعر للمنتج: ${productName}`,
      }));
    }
  }, [productName]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("الاسم مطلوب.");
      return;
    }
    if (!form.phone.trim() || form.phone.trim().length < 6) {
      toast.error("رقم الهاتف غير صحيح، يرجى إدخال رقم صالح.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        service_slug: form.service_slug || null,
        city: form.city || null,
        message: form.message || null,
        product_id: productId || null,
        product_name: productName || null,
      };
      const res = await endpoints.createQuote(payload);
      setDone({ ref: res.ref });
      toast.success(res.message_ar || "تم استلام طلبك بنجاح.");
      onSuccess?.(res);
      setForm({ name: "", phone: "", service_slug: defaultServiceSlug || "", city: "", message: "" });
    } catch (err) {
      toast.error(extractErrorAr(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10 px-4" data-testid={`${testIdPrefix}-success`}>
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
          <Icon name="check-circle" size={36} strokeWidth={2} />
        </div>
        <h3 className="font-heading text-2xl font-bold text-[#0A1628] mb-2">تم استلام طلبك</h3>
        <p className="text-slate-600 max-w-md mx-auto leading-relaxed">
          سنتواصل معك خلال 24 ساعة. رقم الطلب المرجعي:
        </p>
        <div className="mt-3 inline-block px-4 py-2 rounded-full bg-slate-100 font-mono text-sm text-[#0A1628]" dir="ltr">
          {done.ref}
        </div>
        <div className="mt-6">
          <button
            onClick={() => setDone(null)}
            className="text-[#00b8cc] hover:text-[#0A1628] text-sm font-medium transition-colors"
            data-testid={`${testIdPrefix}-send-another`}
          >
            إرسال طلب جديد
          </button>
        </div>
      </div>
    );
  }

  const fieldClass =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0A1628] placeholder:text-slate-400 focus:outline-none focus:border-[#00E5FF] focus:ring-4 focus:ring-cyan-100 transition-colors";
  const labelClass = "block text-sm font-medium text-[#0A1628] mb-1.5";

  return (
    <form onSubmit={submit} className={compact ? "space-y-3" : "space-y-4"} data-testid={testIdPrefix}>
      <div className={compact ? "grid grid-cols-1 gap-3" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
        <div>
          <label className={labelClass}>الاسم الكامل *</label>
          <input
            type="text"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="محمد أحمد"
            className={fieldClass}
            data-testid={`${testIdPrefix}-name`}
          />
        </div>
        <div>
          <label className={labelClass}>رقم الهاتف *</label>
          <input
            type="tel"
            required
            value={form.phone}
            onChange={set("phone")}
            placeholder="+970 59 000 0000"
            className={fieldClass}
            dir="ltr"
            data-testid={`${testIdPrefix}-phone`}
          />
        </div>
      </div>

      <div className={compact ? "grid grid-cols-1 gap-3" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
        <div>
          <label className={labelClass}>الخدمة المطلوبة</label>
          <select
            value={form.service_slug}
            onChange={set("service_slug")}
            className={fieldClass}
            data-testid={`${testIdPrefix}-service`}
          >
            <option value="">اختر خدمة</option>
            {services.map((s) => (
              <option key={s.slug} value={s.slug}>
                {s.title_ar}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>المدينة</label>
          <select
            value={form.city}
            onChange={set("city")}
            className={fieldClass}
            data-testid={`${testIdPrefix}-city`}
          >
            <option value="">اختر المدينة</option>
            {PALESTINIAN_CITIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className={labelClass}>رسالتك</label>
        <textarea
          rows={compact ? 3 : 4}
          value={form.message}
          onChange={set("message")}
          placeholder="اكتب تفاصيل احتياجك، حجم المشروع، أو أي استفسار..."
          className={fieldClass}
          data-testid={`${testIdPrefix}-message`}
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="aman-btn-light w-full justify-center text-base disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid={`${testIdPrefix}-submit`}
      >
        {submitting ? "جاري الإرسال..." : (<><Icon name="send" size={18} strokeWidth={2} /> إرسال الطلب</>)}
      </button>
      <p className="text-xs text-slate-500 text-center">سنحافظ على خصوصية بياناتك ونستخدمها للتواصل معك فقط.</p>
    </form>
  );
}
